/* Tiroir panier — Scoop by Coralie (V18 lot 4)

   Ce module n'implémente **aucune** logique de panier. Il ouvre un panneau,
   y injecte la section `main-cart` rendue par Shopify, et referme. Tout le
   reste — normalisation des cadeaux, regroupement des lignes, quantités,
   totaux, progression — reste la propriété du moteur V17
   (`assets/sbc-cadeaux.js`) et du module unique de commandes
   (`assets/sbc-panier-commandes.js`).

   Conséquence voulue : il n'existe pas de second pipeline. Le tiroir affiche
   exactement ce que `/cart` afficherait.

   Sur `/cart` le tiroir ne s'ouvre jamais : la page EST le panier, et deux
   `#CartForm` simultanés créeraient des identifiants en double.
*/
(() => {
  const racine = document.querySelector('[data-sbc-tiroir]');
  if (!racine) return;

  const SUR_PAGE_PANIER = /\/cart(\/|$|\?)/.test(window.location.pathname);

  const panneau = racine.querySelector('[data-sbc-tiroir-panneau]');
  const corps = racine.querySelector('[data-sbc-tiroir-corps]');
  /* Lot 120 — le pied est rendu en Liquid par `snippets/sbc-panier-tiroir.liquid`
     (lien Voir mon panier puis bouton de paiement). Il n'est ni créé, ni rempli,
     ni déplacé ici : le seul geste JS qui le concerne est `afficherPied()`. */
  const pied = panneau.querySelector('[data-sbc-tiroir-pied]');
  const annonce = racine.querySelector('[data-sbc-tiroir-annonce]');
  const totalAffiche = racine.querySelector('[data-sbc-tiroir-total]');
  const modeleLien = racine.querySelector('[data-sbc-tiroir-lien]');
  const modeleAjout = racine.querySelector('[data-sbc-tiroir-ajout-modele]');
  let ajoutEnAttente = null;

  const effacerMiseEnAvant = () => {
    corps.querySelectorAll('.sbc-cart-line--just-added').forEach((ligne) => {
      ligne.classList.remove('sbc-cart-line--just-added');
    });
    corps.querySelectorAll('[data-sbc-ajout-badge]').forEach((badge) => badge.remove());
  };

  const signatureProprietes = (proprietes) => Object.entries(proprietes || {})
    .filter(([, valeur]) => valeur != null && String(valeur) !== '')
    .map(([cle, valeur]) => [cle, String(valeur)])
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([cle, valeur]) => `${cle}=${valeur}`)
    .join('&');

  const trouverLigneAjoutee = (items) => {
    const lignes = [...corps.querySelectorAll('.cart-line[data-line-keys]')];
    const panier = window.SbcPanier?.panier();
    const cles = new Set();

    items.forEach((ajout) => {
      if (ajout.key) cles.add(String(ajout.key));
      (panier?.items || []).forEach((ligne) => {
        if (String(ligne.variant_id) !== String(ajout.variantId)) return;
        if (signatureProprietes(ligne.properties) === signatureProprietes(ajout.properties)) {
          cles.add(String(ligne.key));
        }
      });
    });

    const parCle = lignes.find((ligne) => (ligne.dataset.lineKeys || '').split(',').some((partie) => {
      const separateur = partie.lastIndexOf(':::');
      return separateur > 0 && cles.has(partie.slice(0, separateur));
    }));
    if (parCle) return parCle;

    const variantes = new Set(items.map((ajout) => String(ajout.variantId)));
    const candidates = lignes.filter((ligne) => variantes.has(ligne.dataset.lineVariant));
    return candidates.length === 1 ? candidates[0] : null;
  };

  const mettreEnAvant = () => {
    if (!ouvert || !ajoutEnAttente) return;
    const cible = trouverLigneAjoutee(ajoutEnAttente);
    if (!cible) return;

    effacerMiseEnAvant();
    cible.classList.add('sbc-cart-line--just-added');
    const badge = modeleAjout?.content.firstElementChild?.cloneNode(true);
    const titre = cible.querySelector('.cart-line__content h2');
    if (badge && titre) titre.insertAdjacentElement('afterend', badge);
    if (annonce) {
      const nom = titre?.textContent.trim() || '';
      annonce.textContent = `${racine.dataset.sbcTiroirAjoutLabel || 'Article ajouté au panier'} : ${nom}`;
    }
    ajoutEnAttente = null;

    requestAnimationFrame(() => {
      if (!ouvert || !cible.isConnected) return;
      const zone = corps.getBoundingClientRect();
      const ligne = cible.getBoundingClientRect();
      const marge = Math.max(12, Math.min(72, (corps.clientHeight - ligne.height) / 2));
      const sommet = Math.max(0, corps.scrollTop + ligne.top - zone.top - marge);
      const reduire = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      corps.scrollTo({ top: sommet, behavior: reduire ? 'auto' : 'smooth' });
    });
  };
  let nomsCourts = {};
  try {
    nomsCourts = JSON.parse(racine.querySelector('[data-sbc-tiroir-titres]')?.textContent || '{}');
  } catch (_) {}
  let reductionOuverte = false;
  racine.addEventListener('toggle', (event) => {
    if (event.target.matches('details.sbc-panier-tiroir__reduction')) {
      reductionOuverte = event.target.open;
    }
  }, true);
  /* Identifiant de la section panier.

     Il n'est pas codé en dur : sur cette boutique il vaut
     `template--29993650913607__main`, une valeur propre au template et au
     thème, qui changerait au moindre duplicata. Le tiroir le découvre au
     premier rendu en lisant l'attribut `data-sbc-cart-section` de la section
     elle-même, puis s'en sert pour les rendus suivants — plus légers. */
  let SECTION = null;

  let ouvert = false;
  let declencheur = null;
  let enChargement = false;
  const CLE_HISTORIQUE = 'sbcPanierTiroir';
  const estEntreePanier = (etat = window.history.state) => !!(etat && etat[CLE_HISTORIQUE] === true);
  let retourEnCours = false;
  let ouvertureApresRetour = null;

  /* Référence capturée avant toute interception : les requêtes du tiroir ne
     doivent pas se redéclencher elles-mêmes. */
  const fetchInterne = window.fetch.bind(window);
  const racineUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const focusables = () => [...panneau.querySelectorAll(
    'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
  )].filter((n) => n.offsetParent !== null || n === document.activeElement);

  const verrouillerPage = (etat) => {
    document.documentElement.classList.toggle('sbc-tiroir-ouvert', etat);
    document.body.classList.toggle('sbc-tiroir-ouvert', etat);
  };

  /* Récupération de la section panier. Une seule requête à la fois : une
     réponse lente ne doit pas écraser une réponse plus récente. */
  let requeteEnCours = null;
  const rendre = async () => {
    if (enChargement) { return requeteEnCours; }
    enChargement = true;
    requeteEnCours = (async () => {
      try {
        /* Premier rendu : on demande la page panier entière et on y prélève la
           section. Rendus suivants : on ne demande que la section. */
        const adresse = SECTION
          ? `${racineUrl}cart?section_id=${encodeURIComponent(SECTION)}`
          : `${racineUrl}cart`;
        const rep = await fetchInterne(adresse, { headers: { Accept: 'text/html' } });
        if (!rep.ok) throw new Error('rendu indisponible');
        const html = await rep.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const neuf = doc.querySelector('[data-sbc-cart-section]');
        if (!neuf) throw new Error('section panier introuvable');
        if (!SECTION) SECTION = neuf.getAttribute('data-sbc-cart-section') || null;
        corps.replaceChildren(neuf);
        /* Une section neuve vient d'être injectée. Un seul événement le dit,
           et tous les consommateurs s'y raccrochent : le module de commandes
           réinstalle ses boutons, l'affichage des cadeaux repeint le bloc de
           progression revenu du serveur dans son état initial.

           Lot 104 — c'était auparavant un `sbc:cart-updated` au `detail` nul,
           que les consommateurs devaient apprendre à ignorer, plus un appel
           direct à `window.sbcCadeaux.peindre()`. Un événement qui ment sur
           son contenu est un piège ; celui-ci dit exactement ce qui s'est
           passé. On ne déclenche toujours PAS de réconciliation ici : le
           moteur l'a déjà faite avant le rendu, la rappeler bouclerait. */
        document.dispatchEvent(new CustomEvent('sbc:section-rendue'));

        afficherPied();
      } catch (_) {
        corps.innerHTML = '<p class="sbc-panier-tiroir__erreur" role="alert">'
          + 'Votre panier n’a pas pu être affiché ici. '
          + `<a href="${racineUrl}cart">Ouvrir la page panier</a>.</p>`;
        afficherPied();
      } finally {
        enChargement = false;
      }
    })();
    return requeteEnCours;
  };

  /* Lot 120 — le pied du tiroir : la page panier puis le paiement.

     Ce qui a été supprimé ici. `epinglerResume()` déplaçait l'`<aside
     class="cart-summary">` entier — titre, sous-total, remise, livraison, code
     promo, bouton, icônes de paiement — dans un pied créé en JavaScript et
     accroché au panneau, sans plafond de hauteur : 494 px sur 844 à 390×844,
     462 px sur 768 à 1366×768 (mesuré le 4 septembre 2026), le corps déroulant
     réduit à 285 px et 237 px. `remonterProgression()` déplaçait de son côté le
     bloc `[data-sbc-gifts]` en tête du corps. Les deux devaient être
     idempotentes, se surveiller l'une l'autre et distinguer « déjà déplacé » de
     « panier vide » — trois correctifs successifs pour un résultat que Liquid
     rend directement au bon endroit.

     Il ne reste donc aucun déplacement de nœud dans ce fichier : la section
     rendue par Shopify est injectée telle quelle, progression comprise, résumé
     compris. Le pied existe dans le HTML dès le chargement de la page ; sa
     seule variable est sa visibilité.

     `#CartForm` tranche : la section ne le rend que sur un panier non vide.
     Sans lui, le bouton n'aurait rien à soumettre — il ne doit pas s'afficher
     au-dessus d'un panier vide. */
  const afficherPied = () => {
    if (!pied) return;
    const form = corps.querySelector('#CartForm');
    const resume = form?.querySelector('.cart-summary');
    pied.hidden = !form;
    if (totalAffiche) {
      totalAffiche.textContent = resume?.querySelector('.cart-summary__row--total strong')?.textContent.trim() || '';
    }
    if (!form) return;
    form.querySelector('.sbc-prog__modalites')?.remove();
    form.querySelectorAll('.cart-line__content h2 a').forEach((lien) => {
      const handle = new URL(lien.href, window.location.href).pathname.split('/products/')[1]?.split('/')[0];
      const nom = nomsCourts[handle];
      if (nom && lien.textContent !== nom) {
        lien.title = lien.textContent.trim();
        lien.textContent = nom;
      }
    });
    const promo = resume?.querySelector('.sbc-discount:not(.sbc-discount--ligne)');
    if (promo && !promo.closest('details.sbc-panier-tiroir__reduction')) {
      const details = document.createElement('details');
      details.className = 'sbc-panier-tiroir__reduction';
      details.open = reductionOuverte;
      const summary = document.createElement('summary');
      summary.textContent = racine.dataset.sbcReductionLabel;
      promo.before(details);
      details.append(summary, promo);
    }
    if (resume && modeleLien && !resume.querySelector('.sbc-panier-tiroir__lien')) {
      resume.insertBefore(modeleLien.content.cloneNode(true), resume.querySelector('.cart-payments'));
    }
  };

  /* Lot 104 — l'observateur de mutations qui surveillait ce corps est retiré.
     Deux chemins seulement injectent une section ici : `rendre()` ci-dessus,
     qui rappelle `afficherPied()` dans la foulée, et le moteur, qui annonce
     `sbc:section-rendue`. Observer le DOM pour deviner ce que le code vient de
     faire lui-même était un contournement ; il rejouait aussi le traitement à
     chaque retouche interne. */

  const ouvrir = async (origine, depuisHistorique = false) => {
    if (SUR_PAGE_PANIER || ouvert) return;
    if (retourEnCours) {
      ouvertureApresRetour = { origine };
      return;
    }
    if (!depuisHistorique) {
      try {
        window.history.pushState({ ...window.history.state, [CLE_HISTORIQUE]: true }, '', window.location.href);
      } catch (_) {}
    }
    declencheur = origine || document.activeElement;
    racine.hidden = false;
    /* Forcer un cycle avant d'animer, sinon la transition ne joue pas. */
    requestAnimationFrame(() => { if (ouvert) racine.classList.add('is-ouvert'); });
    verrouillerPage(true);
    ouvert = true;
    panneau.focus({ preventScroll: true });
    await rendre();
    if (annonce && ouvert && !corps.querySelector('.sbc-cart-line--just-added')) {
      annonce.textContent = 'Panier ouvert et mis à jour.';
    }
  };

  const MS_TRANSITION_FERMETURE = 320;

  const fermer = (depuisHistorique = false) => {
    if (!ouvert) return;
    racine.classList.remove('is-ouvert');
    verrouillerPage(false);
    ouvert = false;
    effacerMiseEnAvant();
    ajoutEnAttente = null;
    const fin = () => { if (!ouvert) racine.hidden = true; };
    /* La durée doit rester alignée sur --motion-slow (300 ms). */
    /* Seul délai restant de ce fichier, et il ne coordonne AUCUNE requête :
       il attend la fin de la transition CSS d'ouverture/fermeture avant de
       remettre `hidden`. Le supprimer sans repli exposerait au cas où
       `transitionend` ne se déclenche pas (animation désactivée, panneau non
       peint) : le voile resterait par-dessus la page. */
    window.setTimeout(fin, MS_TRANSITION_FERMETURE);
    if (declencheur && document.contains(declencheur)) {
      declencheur.focus({ preventScroll: true });
    }
    declencheur = null;
    if (!depuisHistorique && estEntreePanier()) {
      retourEnCours = true;
      window.history.back();
    }
  };

  window.addEventListener('popstate', (e) => {
    retourEnCours = false;
    if (estEntreePanier(e.state)) ouvrir(null, true);
    else fermer(true);
    if (ouvertureApresRetour) {
      const { origine } = ouvertureApresRetour;
      ouvertureApresRetour = null;
      ouvrir(origine);
    }
  });

  racine.addEventListener('click', (e) => {
    if (e.target.closest('[data-sbc-tiroir-fermer]')) { e.preventDefault(); fermer(); return; }
    if (e.target.closest('[data-sbc-tiroir-fond]')) { fermer(); }
  });

  document.addEventListener('keydown', (e) => {
    if (!ouvert) return;
    if (e.key === 'Escape') { e.preventDefault(); fermer(); return; }
    if (e.key !== 'Tab') return;
    /* Piège à focus : la tabulation ne sort jamais du dialogue. */
    const cibles = focusables();
    if (!cibles.length) { e.preventDefault(); panneau.focus(); return; }
    const premier = cibles[0], dernier = cibles[cibles.length - 1];
    if (e.shiftKey && document.activeElement === premier) { e.preventDefault(); dernier.focus(); }
    else if (!e.shiftKey && document.activeElement === dernier) { e.preventDefault(); premier.focus(); }
  });

  /* Ouverture. Le lien panier du header ouvre le tiroir plutôt que de charger
     la page — sauf clic modifié (nouvel onglet) et sauf si le JavaScript est
     indisponible, auquel cas le lien reste un lien. */
  document.addEventListener('click', (e) => {
    /* Lot 104 (constat A-34) — seule une cible explicitement marquée ouvre le
       tiroir. La règle précédente capturait TOUT lien vers `/cart` de la page,
       y compris ceux d'une application tierce ou d'un contenu rédigé par
       Coralie : un lien « voir mon panier » écrit dans une page ne pouvait plus
       mener à la page panier. */
    const lien = e.target.closest('[data-sbc-ouvrir-panier]');
    if (!lien || SUR_PAGE_PANIER) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    ouvrir(lien);
  });

  /* Après un ajout au panier.

     Lot 104 — ce fichier remplaçait `window.fetch` pour voir passer les
     `/cart/add`, puis attendait 700 ms en espérant que le moteur de cadeaux
     ait fini de réconcilier. Deux suppositions, aucune garantie : sur une
     connexion lente le tiroir s'ouvrait sur un panier sans ses cadeaux, et le
     wrapper voyait aussi les requêtes des applications tierces.

     Le moteur annonce maintenant la fin réelle du cycle. Le tiroir s'ouvre
     parce que l'ajout est terminé, pas parce qu'un délai est écoulé. */
  document.addEventListener('sbc:panier-ajout-termine', (event) => {
    if (SUR_PAGE_PANIER) return;
    ajoutEnAttente = event.detail?.items || null;
    if (!ouvert) {
      ouvrir(null).then(mettreEnAvant);
    } else if (!corps.querySelector('[data-sbc-cart-section]')) {
      rendre().then(mettreEnAvant);
    } else {
      mettreEnAvant();
    }
  });

  /* Le moteur annonce un panier réconcilié.

     V19.1 — le tiroir ne redemande plus la section ici. Le moteur compare déjà
     la signature affichée au panier réel et rend la section lui-même quand
     elle est en retard ; l'observateur ci-dessus remet alors le pied en place.
     Redemander systématiquement doublait les requêtes et créait la fenêtre
     pendant laquelle deux boutons de paiement coexistaient.

     Le seul cas où le tiroir doit encore agir est celui où le corps ne
     contient aucune section — première ouverture, ou rendu précédent en
     erreur. */
  document.addEventListener('sbc:cadeaux', () => {
    if (!ouvert) return;
    if (!corps.querySelector('[data-sbc-cart-section]')) { if (!enChargement) rendre(); return; }
    afficherPied();
  });

  /* Le moteur vient de remplacer la section — y compris celle qui vit dans ce
     tiroir quand il est ouvert. Le panier a pu passer de vide à non vide, ou
     l'inverse : la visibilité du pied se recalcule. */
  document.addEventListener('sbc:section-rendue', () => {
    if (!ouvert) return;
    afficherPied();
    mettreEnAvant();
  });

  window.sbcTiroir = { ouvrir, fermer, rendre, estOuvert: () => ouvert };
  if (estEntreePanier()) ouvrir(null, true);
})();

